import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

interface BalanceResponse {
  minutes_remaining: number;
  free_minutes_used: number;
  purchased_minutes_used: number;
}

export default function LowBalanceBanner() {
  const { user } = useAuth();
  const [minutes, setMinutes] = useState<number | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const fetchBalance = useCallback(() => {
    if (!user) return;

    apiFetch<BalanceResponse>("/billing/balance")
      .then((data) => setMinutes(data.minutes_remaining))
      .catch(() => setMinutes(null));
  }, [user]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") fetchBalance();
    };

    window.addEventListener("focus", fetchBalance);
    window.addEventListener("storage", fetchBalance);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener("focus", fetchBalance);
      window.removeEventListener("storage", fetchBalance);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [fetchBalance]);

  if (!user || minutes === null || minutes >= 10 || dismissed) return null;

  return (
    <div className="flex items-center justify-between bg-amber-50 border-b border-amber-200 px-4 py-2.5 sm:px-6">
      <p className="text-sm text-amber-800">
        Low balance! Only <span className="font-semibold">{minutes} minutes</span>{" "}
        remaining.{" "}
        <Link to="/pricing" className="font-medium underline hover:text-amber-900">
          Buy more &rarr;
        </Link>
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="ml-4 text-amber-600 hover:text-amber-800"
        aria-label="Dismiss"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="h-4 w-4"
        >
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>
  );
}
